import React from "react";
import { BookOpen, CircleCheck, Clock, Target } from "lucide-react";

const Courses = ({ course }) => {
    if (!course) return null;

    const { course_name, status, course_progress_percent, milestones } = course;

    const gradientMap = {
        green: "from-green-500 to-emerald-500",
        blue: "from-blue-500 to-cyan-500",
        red: "from-red-500 to-pink-500",
    };

    const courseGradientKey = {
        "Advanced Microservices Architecture": "green",
        "Effective Communication Skills": "green",
        "Advanced SEO & Content Marketing": "blue",
        "Cloud Infrastructure & Scalability": "blue",
        "Customer Relationship Management": "blue",
        "Social Media Strategy & Analytics": "red",
    };

    const key = courseGradientKey[course_name];  
    const gradient = gradientMap[key];
    
    const iconGradient = `bg-gradient-to-br ${gradient}`;
    const progressGradient = `bg-gradient-to-r ${gradient}`;

    return (
        <>
            <div className="p-4 rounded-2xl bg-purple-50 border-2 border-purple-200 dark-mode-card">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-xl ${iconGradient}`}>
                            <BookOpen className="w-4 h-4` text-white" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-[14px] dark:text-white">
                                {course_name}
                            </h4>
                            <p className="text-[11px] text-slate-500">
                                {milestones?.filter(m => m.status.toLowerCase() === "complete").length} 
                                of {milestones?.length} milestones completed
                            </p>
                        </div>
                    </div>
                    <span className={`status-indicator ${status.toLowerCase().replace(/\s+/g, "-")} font-medium text-xs`}>
                        {status}
                    </span>
                </div>

                <div className="space-y-1">
                    <div className="flex items-center justify-between text-[12px]">
                        <span className="font-medium dark:text-white">Course Progress</span>
                        <span className="font-semibold dark:text-white">{course_progress_percent}%</span>
                    </div>
                    <div className="w-full h-3 bg-white rounded-full overflow-hidden border-2 border-purple-200">
                        <div
                            className={`h-full rounded-full ${progressGradient}`}
                            style={{ width: `${course_progress_percent}%` }}
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-3 mt-4">
                <h4 className="font-semibold text-[14px] flex items-center gap-2 dark:text-white">
                    <Target className="w-4 h-4 text-purple-600  dark:text-purple-500[[piub. vl;" />
                    Milestone Progress
                </h4>
                <div className="space-y-3">
                    {milestones?.map((m, index) => {
                        const isCompleted = m.status.toLowerCase() === "complete";
                        const isLast = index === milestones.length - 1;

                        return (
                            <div key={index} className="relative">
                                {!isLast && (
                                    <div
                                        className={`absolute left-8 top-14 w-0.5 h-6 ${
                                            isCompleted ? "bg-green-400" : "bg-slate-300"
                                        }`}
                                    >
                                    </div>
                                )}

                                <div
                                    className={`p-4 rounded-2xl border-2 transition-all ${
                                        isCompleted ? "bg-green-50 border-green-300" : "bg-white border-slate-300 dark:bg-white/10"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-semibold text-[14px] ${
                                                isCompleted
                                                    ? "bg-green-500 border text-white"
                                                    : "bg-slate-100 border-2 border-slate-300"
                                            }`}
                                        >
                                            {isCompleted ? <CircleCheck className="w-5 h-5" /> : index + 1}
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div>
                                                    <h5 className="font-semibold text-[14px] dark:text-white">{m.name}</h5>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <p className="text-[11px] text-slate-500 flex items-center gap-1">
                                                            <Clock className="w-3 h-3" /> Due: {m.due_date}
                                                        </p>
                                                        {m.completed_date && (
                                                            <p className="text-[11px] text-green-600 flex items-center gap-1 font-medium">
                                                                <CircleCheck className="w-3 h-3" /> Completed: {m.completed_date}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`status-indicator ${m.status.toLowerCase().replace(/\s+/g, "-")} text-xs gap-1`}
                                                >
                                                    {isCompleted && <CircleCheck className="w-3 h-3" />}
                                                    {m.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default Courses;
